import React from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import SEO from 'components/shared/SEO';
import MarketTicker from 'components/shared/MarketTicker';
import Spinner from 'components/shared/Spinner';
import { getAsset } from 'helpers/assets';
import { formatAmount, formatAsset } from '@nbx/frontend-helpers/format';
import { isNaNish } from 'helpers/balances';
import styles from './styles.module.scss';

function MarketStats(props) {
  const { t } = useTranslation();
  const tickerData = props.marketTicker.data ? props.marketTicker.data : {};
  const price = props.trades.data.marketPrice;
  const { change, changeAmount, high, low, volume } = tickerData;

  const loading = props.markets.loading || props.trades.loading || props.assets.loading;
  const symbol = getAsset(props.pair);
  const baseAsset = props.pair.split('-')[0];
  const baseAssetPrecision = props.assets.data[baseAsset.toUpperCase()]?.displayPrecision;
  const quoteAsset = props.pair.split('-')[1];
  const quoteAssetPrecision = props.assets.data[quoteAsset.toUpperCase()]?.displayPrecision;
  const tickSize = props.markets.data.markets ? props.markets.data.markets.minTickLength : 3;

  const getChangeFormatted = () => {
    if (props.tickers.loading) return `${formatAmount(0, tickSize)} 0.00%`;
    if (!isNaNish(changeAmount)) return `${formatAmount(changeAmount, tickSize)} ${change}`;
    return '-';
  };

  const getVolumeFormatted = () => {
    if (props.tickers.loading) return formatAmount(0, baseAssetPrecision);
    if (!isNaNish(volume)) return formatAmount(volume, baseAssetPrecision);
    return '-';
  };

  const getBalanceFormatted = () => {
    if (loading) return '-';
    if (props.funds.loading) {
      const basePlaceholder = 10 ** (7 - baseAssetPrecision);
      const quotePlaceholder = 10 ** (7 - quoteAssetPrecision);
      return `${formatAsset(basePlaceholder, baseAsset, props.assets.data)} ${baseAsset} / ${formatAsset(
        quotePlaceholder,
        quoteAsset,
        props.assets.data
      )} ${quoteAsset}`;
    }

    const baseBalance =
      props.funds.data.balances.length > 0 &&
      props.funds.data.balances.find(balance => balance.asset === baseAsset);
    const baseAvailableFunds = baseBalance ? baseBalance.availableBalance : 0;
    const baseAssetString =
      baseBalance && !baseBalance.error
        ? `${formatAsset(baseAvailableFunds, baseAsset, props.assets.data)} ${baseAsset}`
        : '';

    const quoteBalance =
      props.funds.data.balances.length > 0 &&
      props.funds.data.balances.find(balance => balance.asset === quoteAsset);
    const quoteAvailableFunds = quoteBalance ? quoteBalance.availableBalance : 0;
    const quoteAssetString =
      quoteBalance && !quoteBalance.error
        ? `${formatAsset(quoteAvailableFunds, quoteAsset, props.assets.data)} ${quoteAsset}`
        : '-';

    if (props.funds.error || (quoteBalance?.error && baseBalance?.error)) return '-';
    return `${baseAssetString} / ${quoteAssetString}`;
  };

  const changeFormatted = getChangeFormatted();
  const priceFormatted = !isNaNish(price) ? formatAmount(price, tickSize) : '-';
  const highFormatted = !isNaNish(high) ? formatAmount(high, tickSize) : '-';
  const lowFormatted = !isNaNish(low) ? formatAmount(low, tickSize) : '-';
  const volumeFormatted = getVolumeFormatted();
  const balanceFormatted = getBalanceFormatted();

  const getChangeIsUp = () => {
    if (parseFloat(changeAmount) === 0) return undefined;
    return change && !change.includes('-');
  };

  const columns = [
    {
      label: t('market_last_price'),
      value: priceFormatted,
      valueSuffix: symbol
    },
    {
      label: t('market_24h_change'),
      value: changeAmount,
      displayValue: changeFormatted,
      isUp: getChangeIsUp(),
      loading: props.tickers.loading,
      loadingPlaceholder: changeFormatted
    },
    {
      label: t('market_24h_high'),
      value: high,
      displayValue: highFormatted,
      valueSuffix: symbol,
      loading: props.tickers.loading,
      loadingPlaceholder: priceFormatted
    },
    {
      label: t('market_24h_low'),
      value: low,
      displayValue: lowFormatted,
      valueSuffix: symbol,
      loading: props.tickers.loading,
      loadingPlaceholder: priceFormatted
    },
    {
      label: t('market_24h_volume'),
      value: volume,
      displayValue: volumeFormatted,
      valueSuffix: baseAsset,
      loading: props.tickers.loading,
      loadingPlaceholder: volumeFormatted
    }
  ];
  if (props.userId !== null) {
    columns.push({
      label: t('market_available_funds'),
      value: '- / -',
      displayValue: balanceFormatted,
      loading: props.funds.loading,
      loadingPlaceholder: balanceFormatted
    });
  }

  return (
    <div
      className={classnames(styles.marketStatsRow, { [styles.marketStatsRowNoUser]: props.userId === null })}
      key={`${props.pair}-stats`}>
      {priceFormatted !== '-' && <SEO title={`${priceFormatted} ${baseAsset}/${quoteAsset}`} />}
      {loading && (
        <div className={styles.spinnerContainer}>
          <Spinner delayed />
        </div>
      )}
      {!loading &&
        columns.map((column, index) => (
          <div
            className={styles.animatedColumn}
            style={{ animation: `${styles.fadeIn} 0.5s ${index * 0.05}s forwards` }}
            key={`column-${column.label}`}>
            <MarketTicker {...column} index={index} pair={props.pair} key={`column-${column.label}`} />
          </div>
        ))}
    </div>
  );
}

MarketStats.propTypes = {
  pair: PropTypes.string.isRequired,
  tickers: PropTypes.object.isRequired,
  marketTicker: PropTypes.object.isRequired,
  trades: PropTypes.object.isRequired,
  funds: PropTypes.object.isRequired,
  markets: PropTypes.object.isRequired,
  assets: PropTypes.object.isRequired,
  userId: PropTypes.string,
  preferences: PropTypes.object.isRequired
};

MarketStats.defaultProps = {
  userId: null
};

export default MarketStats;
